import { FC, useEffect } from "react";
import {
  UseFormRegisterReturn,
  useWatch,
  useFormContext,
} from "react-hook-form";

type SliderProps = {
  label: string;
  id: string;
  error?: string;
  register: UseFormRegisterReturn;
  min?: number;
  max?: number;
  step?: number;
  isCurrency?: boolean;
  defaultValue?: number;
  classes?: string;
};

function formatValue(value: number, isCurrency: boolean, max: number): string {
  const formatted = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  if (isCurrency) {
    return value > max ? `${max.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")}+ Kč` : `${formatted} Kč`;
  }
  return value > max ? `+${max}` : `${value}`;
}

const Slider: FC<SliderProps> = ({
  label,
  id,
  error,
  min = 0,
  max = 100,
  step = 1,
  isCurrency = false,
  defaultValue = 50,
  classes,
}) => {
  const { control, setValue, register } = useFormContext();
  const watchedValue = useWatch({ control, name: `${id}-controller`, defaultValue });
  const numericValue = Number(watchedValue);
  const value = Number.isFinite(numericValue) ? numericValue : defaultValue;

  const formattedValue = formatValue(value, isCurrency, max);

  useEffect(() => {
    setValue(id, formattedValue);
  }, [formattedValue, setValue, id]);

  return (
    <label
      htmlFor={id}
      className={`${classes && classes} grid p-3 bg-neutral-900 border border-neutral-300/10 rounded focus-within:border-neutral-300/20 transition-all gap-4`}
    >
      <span className="mono flex justify-between gap-8">
        <span className={`${error ? "opacity-100" : "opacity-60"} grow`}>
          {label}
        </span>
        <span className="text-xs">{formattedValue}</span>
      </span>

      <input
        id={`${id}-controller`}
        type="range"
        min={min}
        max={max + step}
        step={step}
        value={value}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={formattedValue}
        className="text-white text-lg outline-none bg-transparent autofill:bg-yellow-200"
        onChange={(e) => {
          const newValue = parseFloat(e.target.value);
          const newFormattedValue = formatValue(newValue, isCurrency, max);
          setValue(`${id}-controller`, newValue);
          setValue(id, newFormattedValue);
        }}
      />
      <input type="hidden" {...register(id)} value={formattedValue} />
    </label>
  );
};

export default Slider;
